/* Verbatim port of matrixCellFor + renderMatrix from service/review/
 * index.html (~2674-2710) — §4's primary surface: the entity × platform
 * matrix table. matrixEntities/MATRIX_PLATFORMS/platformOf were already
 * ported into derive/entity.ts by an earlier task — imported from there
 * rather than re-ported.
 *
 * PARAMETERIZED (rule a): renderMatrix read the global STATE (via
 * matrixEntities()/platformOf() and STATE.assets) — takes `state:
 * AppPayloadState` as an explicit first parameter.
 *
 * DEVIATION (TS2362): `(b.scope === 'global') - (a.scope === 'global')`
 * (original ~2676) subtracts two booleans, relying on JS's implicit
 * boolean->number coercion in arithmetic — TS's strict mode rejects
 * arithmetic directly on `boolean` operands. Wrapped in `Number(...)`,
 * the same accommodation already applied to the identical pattern in
 * views/exec.ts's chanCards sort (`Number(b.scope === 'global') -
 * Number(a.scope === 'global')`); numeric result is unchanged (`Number(true)
 * === 1`, same as the coercion JS would have performed anyway).
 *
 * F3 (wave B): innerHTML → lit render(). `matrixCellFor` (module-local,
 * not a lib/templates.ts twin candidate — it's matrix.ts-only) now returns
 * a TemplateResult and takes `state` as a NEW second parameter (it didn't
 * read STATE in the original at all) purely so its own `.mcell[data-id]`
 * span can carry its own `@click=${() => openDrawer(state, a.id)}` —
 * replacing the trailing `tbl.querySelectorAll('.mcell[data-id]')`
 * delegated wiring, which targeted exactly this span (never the "none"/
 * "+N more" spans matrixCellFor doesn't render, which correctly get no
 * click handler either way, matching the original selector's scoping).
 *
 * D1 (a11y, pill-archetypes review): the drill cell now carries role="button"
 * + tabindex="0" + an Enter/Space keydown so it's keyboard-focusable and
 * activatable — its :focus-visible ring was previously unreachable on a bare
 * <span>. Covered by tests/unit/matrix.test.ts.
 */
import { html, nothing, render } from 'lit';
import { MATRIX_PLATFORMS, matrixEntities, platformOf } from '../derive/entity.ts';
import { BAD_STATUS, canEdit } from '../derive/axes.ts';
import { domainOf, favForDomain } from '../lib/format.ts';
import { openDrawer } from '../ui/drawer.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';
import type { NormalizedAsset } from '../types/contract.ts';

function matrixCellFor(assets: NormalizedAsset[], state: AppPayloadState) {
  // pick the flagship: global scope first, then most-followed
  // (String(...) inside parseInt is the exact coercion the untyped code did)
  assets.sort((a, b) => (Number(b.scope === 'global') - Number(a.scope === 'global')) || ((parseInt(String(b.fields && b.fields.followers))||0) - (parseInt(String(a.fields && a.fields.followers))||0)));
  const a = assets[0];
  if (!a) return nothing; // callers only pass non-empty hits — narrows for TS
  const extra = assets.length - 1;
  let cls = 'pub', label: string = a.status || '—';
  if (a.category === 'owned'){ cls = 'ok'; label = 'owned · active'; }
  else if (canEdit(a)){ cls = 'edit'; label = a.status === 'active' ? (a.scope === 'executive' ? 'personal · active' : 'client-run') : a.status; }
  else if (a.category === 'claimable'){ cls = 'warn'; label = a.status === 'unclaimed' ? 'unclaimed' : a.status === 'unknown' ? 'claim unverified' : a.status; }
  if (BAD_STATUS.includes(a.status)) { cls = 'warn'; label = a.status; } // R5: hygiene gap = amber
  if (a.sentiment === 'negative') cls = 'bad'; // R5: only Undesirable content earns red
  // D1: this cell drills into the drawer, so it's a real interactive control —
  // a bare <span> can't take focus, so its CSS :focus-visible ring (app.css)
  // was unreachable by keyboard. role="button"+tabindex="0" put it in the tab
  // order (DOM order = the table's natural left-to-right, top-to-bottom read),
  // and the keydown mirrors the @click so Enter/Space activate it (Space's
  // default page-scroll suppressed). The non-interactive "none"/"+N more"
  // cells in renderMatrix carry no handler and stay unfocusable by design.
  const open = () => openDrawer(state, a.id);
  return html`<span class="mcell ${cls}" data-id="${a.id}" title="${a.title}" role="button" tabindex="0"
    @click=${open}
    @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
    >${label}${extra > 0 ? html`<span class="mn">+${extra}</span>` : nothing}</span>`;
}
export function renderMatrix(state: AppPayloadState): void {
  const tbl = document.getElementById('matrixTbl')!;
  const ents = matrixEntities(state);
  const rows = ents.map((ent) => {
    const mine = state.assets.filter((a) => ent.match(a));
    let placed = 0;
    const cells = MATRIX_PLATFORMS.map(p => {
      const hits = mine.filter((a) => platformOf(a) === p);
      placed += hits.length;
      if (!hits.length) return html`<td><span class="mcell none" title="no ${p} presence found for ${ent.name}">—</span></td>`;
      return html`<td>${matrixCellFor(hits, state)}</td>`;
    });
    const rest = mine.length - placed;
    const restCell = rest > 0
      ? html`<td><span class="mcell pub" title="listings, reviews, coverage and other surfaces for ${ent.name} — see the groups below">${rest} more</span></td>`
      : html`<td><span class="mcell none">—</span></td>`;
    // entity favicon: declared domain first, else its website asset's domain
    const entDom = ent.domain || (() => { const w = mine.find((a) => a.url && (a.assetType === 'website' || a.isCoreEntity)); return w ? domainOf(w.url) : null; })();
    const fav = ent.kind === 'executive' ? nothing : (entDom ? html`<img class="efav" src="${favForDomain(entDom)}" alt="">` : nothing);
    return html`<tr><td class="entity">${fav}${ent.name}<span class="er">${ent.role}</span></td>${cells}${restCell}</tr>`;
  });
  render(html`<thead><tr><th>Entity</th>${MATRIX_PLATFORMS.map(p => html`<th>${p}</th>`)}<th>Other</th></tr></thead><tbody>${rows}</tbody>`, tbl);
}
